/**
 * Pacing (D153) — a pack's real-time clock, declared in data.
 *
 * @remarks
 * The engine's clock is turns (Mike's ruling, `the-descent-arc.md` §8, and
 * the clock conference of 2026-09-07). What a pack declares here is a PACE:
 * how many milliseconds of the player's silence the shell should convert
 * into one `wait` — and `wait` is already a real command (D52), so the turn
 * pipeline, the scheduler, saves, replay and export learn nothing. The engine
 * never reads a clock; the shell converts seconds to turns before they reach
 * it.
 *
 * The pace is CONDITIONAL — the ambience walk (`variants` + `fallback`,
 * first match wins), the fifth deliberate mirror — because an always-on
 * auto-`wait` would drain turns in a foyer, rotate the wait-acknowledgement
 * pool and fire occurrence triggers out of silence. `'none'` is the resting
 * value: no clock runs here.
 *
 * `countdown` names the deadline EVENT rather than any number of seconds:
 * the engine answers with turns remaining to that occurrence, the shell
 * multiplies by the rate, and the number is honest under every route — spend
 * a turn typing and it drops by a whole tick. D244 adds its other face: a
 * countdown that reads a COUNTER (`counterId` in place of `eventId`) — the
 * suit's canister, which a tick spends a minute a turn and which remembers
 * where it stopped, so the number on screen is the number of turns left
 * however many exposures came before it.
 *
 * `enabledByDefault` is the pack's, not the engine's (Mike: "configurable by
 * the game builder, in the data"); the player's preference overrides it. A
 * pack that ships the clock ON owes a `disclosure` — the loader refuses the
 * one without the other — because WCAG 2.2.1 wants the limit adjustable
 * BEFORE it is met, and the disclosure is where the shell says so.
 *
 * @packageDocumentation
 */

import { z } from 'zod';

import { conditionSchema } from './conditions.js';
import { counterIdSchema, eventDefinitionIdSchema } from './identifiers.js';
import { nonBlankStringSchema } from './primitives.js';

/** A running clock: the rate, and optionally what it counts toward. */
export const runningPaceSchema = z
	.strictObject({
		/** Milliseconds of silence that cost one `wait`. Below a second is a game about typing speed. */
		autoWaitMs: z.int().min(1000),
		countdown: z
			.union([
				z
					.strictObject({
						/** The pending event the number counts toward — turns remaining × the rate is the seconds shown. */
						eventId: eventDefinitionIdSchema,
						/** The instrument speaking the number — `ATMOSPHERE`, not a game HUD. */
						label: nonBlankStringSchema,
					})
					.readonly(),
				z
					.strictObject({
						/** D244: the counter the number IS — its value is the turns remaining, × the rate the seconds shown. */
						counterId: counterIdSchema,
						/** The instrument speaking the number — `RUNS — SUIT O2`, not a game HUD. */
						label: nonBlankStringSchema,
					})
					.readonly(),
			])
			.optional(),
	})
	.readonly();

/** A running clock — see {@link runningPaceSchema}. */
export type RunningPace = z.infer<typeof runningPaceSchema>;

/** One pace slot's value: a running clock, or none. */
export const paceValueSchema = z.union([z.literal('none'), runningPaceSchema]);

/** A pace slot's value — see {@link paceValueSchema}. */
export type PaceValue = z.infer<typeof paceValueSchema>;

/** One conditional pace choice: this pace, whenever this condition holds — `conditionSchema` verbatim (the D96 rule). */
export const paceVariantSchema = z
	.strictObject({
		condition: conditionSchema,
		pace: paceValueSchema,
	})
	.readonly();

/** One conditional pace choice — see {@link paceVariantSchema}. */
export type PaceVariant = z.infer<typeof paceVariantSchema>;

/** A possibly-conditional pace — the ambience shape carrying a pace value. */
export const conditionalPaceSchema = z.union([
	paceValueSchema,
	z
		.strictObject({
			fallback: paceValueSchema,
			variants: z.array(paceVariantSchema).min(1).readonly(),
		})
		.readonly(),
]);

/** A possibly-conditional pace — see {@link conditionalPaceSchema}. */
export type ConditionalPace = z.infer<typeof conditionalPaceSchema>;

/** The pack's pacing block — see the module remarks. */
export const packPacingSchema = z
	.strictObject({
		/** What the shell tells the player before the clock can be met — required when the clock ships on. */
		disclosure: nonBlankStringSchema.optional(),
		/** Whether the clock runs for a player who has expressed no preference. */
		enabledByDefault: z.boolean().default(false),
		pace: conditionalPaceSchema,
	})
	.readonly()
	.refine((pacing) => !pacing.enabledByDefault || pacing.disclosure !== undefined, {
		message:
			'a pack that ships its clock on by default must disclose it — the limit has to be adjustable before it is met (WCAG 2.2.1), and the disclosure is where the player learns there is one',
		path: ['disclosure'],
	});

/** A pack's pacing block — see {@link packPacingSchema}. */
export type PackPacing = z.infer<typeof packPacingSchema>;
